import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtBRL, fmtNumber, fmtPct, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates, periodLabel as fmtPeriodLabel } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { ExecutiveHero, fmtCompactBRL } from "../components/intelligence/ExecutiveHero";
import { KpiTile, SparkLine, SplitMeter } from "../components/intelligence/KpiTile";
import { AttentionPanel } from "../components/intelligence/AttentionPanel";
import { buildExecutiveIntel, buildExecutiveSummary } from "../lib/intelligence/executive";
import { buildMonthRoute } from "../lib/intelligence/month-route";
import { memoryPath } from "../lib/intelligence/memory-path";
import {
  IconAlertTriangle, IconLightbulb, IconInfo, IconArrowUp, IconArrowDown, IconMinus,
} from "../components/icons";
import type {
  TransportOverviewDto,
  TransportCteDashboardDto,
  TransportFinancialDto,
} from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

// ── helpers ───────────────────────────────────────────────────────────────────

function TrendIcon({ trend }: { trend: "up" | "down" | "flat" }) {
  if (trend === "up")   return <IconArrowUp   className="w-3.5 h-3.5" />;
  if (trend === "down") return <IconArrowDown className="w-3.5 h-3.5" />;
  return <IconMinus className="w-3.5 h-3.5" />;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ExecutivePage() {
  const { user } = useAuth();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: overview, isLoading: overviewLoading, isError: overviewError, refetch: overviewRefetch } = useQuery({
    queryKey: ["transport", "overview", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportOverviewDto>(
        `/dashboards/transport/overview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const { data: cte, isLoading: cteLoading, isError: cteError, refetch: cteRefetch } = useQuery({
    queryKey: ["transport", "cte", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportCteDashboardDto>(
        `/dashboards/transport/cte?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const { data: fin, isLoading: finLoading, isError: finError, refetch: finRefetch } = useQuery({
    queryKey: ["transport", "financial", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportFinancialDto>(
        `/dashboards/transport/financial?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const isLoading = overviewLoading || cteLoading || finLoading;

  // ── Escalares do resumo CTE ───────────────────────────────────────────────
  const s = cte?.summary;
  const p = cte?.prevSummary;

  // Base da participação dos clientes: o faturamento TOTAL do período (o mesmo
  // "Total" do cabeçalho da lista e da DRE). `topCustomers` traz só os 10 maiores;
  // dividir pela soma deles inflava a participação de todos. A soma dos 10 só
  // entra se o resumo de CT-e não carregou.
  const topCustomersSum = (overview?.topCustomers ?? []).reduce((a, c) => a + c.totalValue, 0);
  const customerShareBase = s && s.totalValue > 0 ? s.totalValue : topCustomersSum;

  // ── Deltas ────────────────────────────────────────────────────────────────
  const revDelta       = computeDelta(s?.totalValue,  p?.totalValue,  { direction: "higher_is_better" });
  const countDelta     = computeDelta(s?.count,       p?.count,       { direction: "higher_is_better" });
  const ticketDelta    = computeDelta(s?.avgTicket,   p?.avgTicket,   { direction: "higher_is_better" });
  const receivedDelta  = computeDelta(fin?.receivedMtd, fin?.receivedPrevMtd, { direction: "higher_is_better" });
  const paidDelta      = computeDelta(fin?.paidMtd,   fin?.paidPrevMtd,   { direction: "higher_is_worse" });
  const arOverdueDelta = computeDelta(fin?.arOverdueTotal, fin?.arOverduePrev1M, { direction: "higher_is_worse" });

  // ── Inteligência: pontos de atenção, leituras e resumo (mesmos dados da tela) ──
  const intel = useMemo(
    () => buildExecutiveIntel({ cte, financial: fin, overview, from, to }),
    [cte, fin, overview, from, to],
  );
  const summary = useMemo(() => buildExecutiveSummary(intel, cte), [intel, cte]);

  // ── Dados dos gráficos ────────────────────────────────────────────────────
  const customerList = useProgressiveList(overview?.topCustomers ?? []);
  const debtorList   = useProgressiveList(fin?.topDebtors ?? []);
  const maxDebtorAmount = Math.max(...(fin?.topDebtors ?? []).map((d) => d.overdueAmount), 1);

  const dailyChartData = (overview?.cteByDay ?? []).map((r) => ({
    date: r.date,
    faturamento: r.totalValue,
  }));

  const flowChartData = (fin?.monthlyFlow ?? []).map((r) => ({
    month: r.month,
    receber: r.received,
    pagar:   r.paid,
  }));

  // ── Abertura: rota do período e números de apoio (os mesmos do antigo banner) ──
  const route = useMemo(
    () => buildMonthRoute({ cteByDay: overview?.cteByDay ?? [], period: filters.period, from, to }),
    [overview, filters.period, from, to],
  );
  const companyName = user?.isDemo && user.companyName ? user.companyName : "Transmasi";
  const clientesAtivos = cte?.summary.activeCustomers ?? 0;

  // ── Séries dos mini-gráficos dos indicadores (só dados já carregados) ──
  const daily = overview?.cteByDay ?? [];
  const dailyRevenue = daily.map((d) => d.totalValue);
  const dailyCount = daily.map((d) => d.count);
  const dailyTicket = daily.filter((d) => d.count > 0).map((d) => d.totalValue / d.count);
  const monthlyReceived = (fin?.monthlyFlow ?? []).map((m) => m.received);
  const monthlyPaid = (fin?.monthlyFlow ?? []).map((m) => m.paid);
  const topCustomer = overview?.topCustomers?.[0];
  const leaderShare = topCustomer && s && s.totalValue > 0 ? (topCustomer.totalValue / s.totalValue) * 100 : null;
  const topDebtor = fin?.topDebtors?.[0];
  // "vs. setembro de 2026, mesmo período" → "vs. set."
  const compMonth = cte?.comparisonLabel?.match(/^vs\.\s+(\S+) de \d{4}/);
  const compShort = compMonth?.[1] ? `vs. ${compMonth[1].slice(0, 3)}.` : (cte?.comparisonLabel ?? null);

  return (
    <div className="space-y-6">

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <PageHeader
        title="Visão Executiva"
        subtitle={`Saúde operacional e financeira da ${user?.isDemo && user.companyName ? user.companyName : "Transmasi"}`}
      />

      {(overviewError || cteError || finError) && <QueryErrorBanner onRetry={() => { overviewRefetch(); cteRefetch(); finRefetch(); }} />}

      {/* ── Abertura: o período como uma rota (manchete + faturamento acumulado) ── */}
      <ExecutiveHero
        route={route}
        summary={summary}
        companyName={companyName}
        revenue={s?.totalValue ?? null}
        prevRevenue={p && p.totalValue > 0 ? p.totalValue : null}
        comparisonLabel={(cte?.comparisonLabel ?? "período anterior").replace(/^vs\.\s*/i, "")}
        loading={isLoading}
      />

      {/* ── Pontos de atenção (as 5 perguntas + memória de cálculo) ─────────── */}
      <AttentionPanel points={intel.attention} loading={isLoading} />

      {/* ── Indicadores do período: cards com mini-gráfico (dados que a tela já recebe) ── */}
      <section aria-labelledby="indicadores-titulo" className="space-y-3">
        <h2 id="indicadores-titulo" className="font-display font-semibold text-lg text-gray-900">Indicadores do período</h2>

        <div>
          <h3 className="text-xs font-semibold text-gray-600 mb-1.5">Operação</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <KpiTile
              label="Faturamento CT-e"
              value={s ? fmtBRL(s.totalValue) : "—"}
              delta={revDelta}
              deltaLabel={compShort}
              memory={{ cardKey: "cte-receita-mes", periodFrom: from, periodTo: to }}
              visual={<SparkLine values={dailyRevenue} label="Faturamento por dia no período" />}
              footnote="Faturamento por dia. O ponto marca o dia mais recente"
              loading={isLoading}
            />
            <KpiTile
              label="CT-es emitidos"
              value={s ? fmtNumber(s.count) : "—"}
              delta={countDelta}
              deltaLabel={compShort}
              visual={<SparkLine values={dailyCount} label="CT-es por dia no período" />}
              footnote="CT-es autorizados por dia. O ponto marca o dia mais recente"
              loading={isLoading}
            />
            <KpiTile
              label="Ticket médio CT-e"
              value={s ? fmtBRL(s.avgTicket) : "—"}
              delta={ticketDelta}
              deltaLabel={compShort}
              visual={<SparkLine values={dailyTicket} label="Ticket médio por dia no período" />}
              footnote="Ticket médio de cada dia"
              loading={isLoading}
            />
            <KpiTile
              label="Tomadores ativos"
              value={s ? fmtNumber(clientesAtivos) : "—"}
              footnote={leaderShare != null && topCustomer
                ? <>Maior tomador: <span className="font-semibold text-gray-900">{topCustomer.name}</span>, com {fmtPct(leaderShare)} do faturamento</>
                : "Clientes com CT-e autorizado no período"}
              loading={isLoading}
            />
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold text-gray-600 mb-1.5">Financeiro</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <KpiTile
              label="Recebido no período"
              value={fin ? fmtBRL(fin.receivedMtd) : "—"}
              delta={receivedDelta}
              deltaLabel="vs. período anterior"
              memory={{ cardKey: "recebido-no-periodo", periodFrom: from, periodTo: to }}
              visual={<SparkLine values={monthlyReceived} label="Recebido nos últimos meses" />}
              footnote="Recebido nos últimos meses. O ponto é o mês atual, ainda em andamento"
              loading={isLoading}
            />
            <KpiTile
              label="Pago no período"
              value={fin ? fmtBRL(fin.paidMtd) : "—"}
              delta={paidDelta}
              deltaLabel="vs. período anterior"
              visual={<SparkLine values={monthlyPaid} label="Pago nos últimos meses" />}
              footnote="Pago nos últimos meses. O ponto é o mês atual, ainda em andamento"
              loading={isLoading}
            />
            <KpiTile
              label="A receber em aberto"
              value={fin ? fmtBRL(fin.arOpenTotal) : "—"}
              deltaLabel={null}
              memory={{ cardKey: "a-receber-em-aberto" }}
              visual={fin && fin.arOpenTotal > 0 ? (
                <SplitMeter parts={[
                  { label: "A vencer", value: fin.arOpenTotal - fin.arOverdueTotal, display: fmtCompactBRL(fin.arOpenTotal - fin.arOverdueTotal), tone: "brand" },
                  { label: "Vencido", value: fin.arOverdueTotal, display: fmtCompactBRL(fin.arOverdueTotal), tone: "danger" },
                ]} />
              ) : null}
              footnote="Posição atual"
              loading={isLoading}
            />
            <KpiTile
              label="Inadimplência"
              value={fin ? fmtBRL(fin.arOverdueTotal) : "—"}
              delta={arOverdueDelta}
              deltaLabel="vs. mesmo dia 1 mês atrás"
              status={fin ? (fin.arOverdueTotal > 100_000 ? "critical" : fin.arOverdueTotal > 0 ? "warning" : null) : null}
              memory={{ cardKey: "inadimplencia-total" }}
              footnote={topDebtor
                ? <>Maior devedor: <span className="font-semibold text-gray-900">{topDebtor.name}</span>, {fmtCompactBRL(topDebtor.overdueAmount)} vencidos há até {fmtNumber(topDebtor.oldestDueDays)} dias</>
                : "Contas a receber vencidas e não pagas"}
              loading={isLoading}
            />
          </div>
        </div>
      </section>

      {/* ── Insights automáticos ────────────────────────────────────────────── */}
      {intel.readings.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <IconLightbulb className="w-3.5 h-3.5 text-tm-500" />
            Insights do período
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {intel.readings.map((ins) => {
              const color = ins.trend === "up" ? "text-tm-600" : ins.trend === "down" ? "text-danger-600" : "text-gray-500";
              return (
                <div key={ins.id} className="card px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${color}`}>
                      <TrendIcon trend={ins.trend} />
                    </span>
                    <p className="text-sm font-semibold text-gray-800">{ins.title}</p>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed mb-2">{ins.body}</p>
                  <div className="flex items-end justify-between gap-2">
                    <p className={`text-lg font-bold ${color}`}>{ins.metric}</p>
                    {ins.memory && (
                      <Link
                        to={memoryPath(ins.memory)}
                        className="text-xs font-medium text-tm-700 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500"
                      >
                        Ver cálculo
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Gráficos ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Faturamento CT-e por Dia"
          subtitle={`${fmtPeriodLabel(filters.period)}, apenas CT-es autorizados`}
          height={240}
          loading={overviewLoading}
          isEmpty={dailyChartData.length === 0}
          emptyMessage="Sem CT-es autorizados no período selecionado."
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyChartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fat" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDateShort}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${(v / 1_000).toFixed(0)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <Tooltip
                formatter={(v: number) => [fmtBRL(v), "Faturamento"]}
                labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Area
                type="monotone"
                dataKey="faturamento"
                stroke="var(--brand)"
                strokeWidth={2}
                fill="url(#grad-fat)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Recebimentos × Pagamentos"
          subtitle="Últimos 5 meses, fluxo financeiro realizado"
          height={240}
          loading={finLoading}
          isEmpty={flowChartData.length === 0}
          emptyMessage="Sem dados de fluxo para exibir."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={flowChartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-recebido" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="grad-pago" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#F59E0B" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${(v / 1_000).toFixed(0)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <Tooltip
                formatter={(v: number, name: string) => [fmtBRL(v), name === "receber" ? "Recebido" : "Pago"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(n) => n === "receber" ? "Recebido" : "Pago"}
              />
              <Bar dataKey="receber" fill="url(#grad-recebido)" radius={[4, 4, 0, 0]} name="receber" maxBarSize={36} />
              <Bar dataKey="pagar"   fill="url(#grad-pago)"     radius={[4, 4, 0, 0]} name="pagar"   maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Top Tomadores ──────────────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Top Tomadores por Faturamento</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Tomador de serviço (clientecon) · CT-es autorizados · {fmtPeriodLabel(filters.period)}
            </p>
          </div>
          {s && s.totalValue > 0 && (
            <span className="text-xs text-gray-500 hidden sm:block">
              Total: {fmtBRL(s.totalValue)}
            </span>
          )}
        </div>

        {overviewLoading && (
          <div className="p-6 text-sm text-gray-400 text-center">Carregando…</div>
        )}

        {!overviewLoading && (overview?.topCustomers ?? []).length === 0 && (
          <div className="p-6 text-sm text-gray-400 text-center">
            Sem dados de tomadores no período.
          </div>
        )}

        <div className="divide-y divide-surface-border">
          {customerList.visible.map((c, i) => {
            const sharePct = customerShareBase > 0 ? (c.totalValue / customerShareBase) * 100 : 0;
            return (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-tm-500 rounded-full transition-all"
                      style={{ width: `${Math.min(Math.max(sharePct, 2), 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{c.formatted}</p>
                  <p className="text-xs text-gray-400">
                    {sharePct.toFixed(1)}% · {fmtNumber(c.cteCount)} CT-es
                  </p>
                </div>
              </div>
            );
          })}
          <LoadMoreBar list={customerList} label="clientes" />
        </div>
      </div>

      {/* ── Maiores inadimplentes ──────────────────────────────────────────── */}
      {(fin?.topDebtors ?? []).length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-danger-500" />
              Maiores Inadimplentes
            </p>
            <span className="text-xs text-gray-400">Vencido em aberto</span>
          </div>
          <div className="divide-y divide-surface-border">
            {debtorList.visible.map((d, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{d.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-danger-500 rounded-full transition-all"
                      style={{ width: `${Math.min(Math.max((d.overdueAmount / maxDebtorAmount) * 100, 2), 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-danger-600">{d.formatted}</p>
                  <p className="text-xs text-gray-400">Mais antigo: {d.oldestDueDays} dias</p>
                </div>
              </div>
            ))}
            <LoadMoreBar list={debtorList} label="devedores" />
          </div>
        </div>
      )}

      {/* ── Info de confiabilidade ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-gray-400 pb-2">
        <IconInfo className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          Faturamento e tomadores baseados em CT-es autorizados (status=2).
          Despesas operacionais não exibidas aqui (ver tela DRE).
          Contas a pagar podem estar parcialmente sincronizadas.
        </span>
      </div>

    </div>
  );
}
